"use client";

import Link from "next/link";
import Image from "next/image";
import { createPortal } from "react-dom";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState, useRef } from "react";
import { useTheme } from "next-themes";
import { useLanguage, LANGUAGE_OPTIONS } from "@/components/LanguageProvider";
import { fetchWithAuth } from "@/lib/fetchWithAuth";
import {
  normalizeUserProfile,
  toCoreSessionUpdate,
  type NormalizedProject,
  type NormalizedUserProfile,
} from "@/lib/apiResponse";
import { fetchSiteWorkspaces } from "@/lib/siteWorkspaces";
import {
  AGENT_HUB_ITEMS,
  CHAT_HISTORY_PATH,
  getAgentSessionHref,
} from "@/lib/agentHub";
import {
  listAgentSessions,
  loadAgentSession,
  type AgentSessionListItem,
} from "@/lib/agentRunTransport";
import {
  SIDEBAR_SESSION_SURFACES,
  combineSidebarSessionPages,
  mergeSidebarSessionFeed,
} from "@/lib/sidebarSessionFeed";
import { openPlanCatalog, openSettings } from "@/lib/settingsHash";
import { buildLoginHref } from "@/lib/authRedirect";
import { PRODUCT_SURFACE_LOCK_FALLBACK_PATH } from "@/lib/temporaryProductLock";
import { useProductSurfaceLockActive } from "@/components/ProductSurfaceLockConsole";
import { UserAvatar } from "@/components/common/UserAvatar";
import {
  SquarePen,
  MessageCircle,
  Folder,
  LibraryBig,
  PanelLeft,
  MoreVertical,
  Star,
  Pencil,
  FolderPlus,
  FolderMinus,
  Trash2,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Monitor,
  Sun,
  Moon,
  Globe,
  Settings,
  Zap,
  MessageSquare,
  User,
  X,
  Check,
  LogOut,
  Building2,
} from "lucide-react";

const SIDEBAR_PROJECT_PREVIEW_LIMIT = 4;

async function fetchSidebarSiteProjects(): Promise<NormalizedProject[]> {
  const sites = await fetchSiteWorkspaces();
  return sites.map((site) => ({
    id: site.id,
    name: site.name,
    description: site.description,
    avatar_file_id: null,
    created_at: site.createdAt ?? "",
    latest_chat_at: site.updatedAt ?? site.createdAt ?? null,
    sessions: [],
    kind: "site",
    location: site.location,
  }));
}

type SidebarSession = AgentSessionListItem;

type SidebarSessionUpdate = Partial<
  Pick<SidebarSession, "title" | "is_starred" | "project_id">
>;

const SIDEBAR_SESSION_PAGE_SIZE = 20;

async function fetchSidebarSessionPage(page: number) {
  const settled = await Promise.allSettled(
    SIDEBAR_SESSION_SURFACES.map((surface) =>
      listAgentSessions(page, SIDEBAR_SESSION_PAGE_SIZE, surface),
    ),
  );
  const pages = settled.flatMap((result) =>
    result.status === "fulfilled" ? [result.value] : [],
  );

  if (pages.length === 0) {
    const rejected = settled.find((result) => result.status === "rejected");
    throw rejected && rejected.status === "rejected"
      ? rejected.reason
      : new Error("사이드바 세션 목록을 불러오지 못했습니다.");
  }

  return combineSidebarSessionPages(pages);
}

function RiskAssessmentAgentIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      className={className}
      width="24"
      height="24"
      viewBox="0 0 48 48"
      fill="none"
      aria-hidden="true"
    >
      <circle
        cx="8.5"
        cy="10.5"
        r="6.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="3.1"
      />
      <rect
        x="2"
        y="31"
        width="13"
        height="13"
        rx="1.4"
        fill="none"
        stroke="currentColor"
        strokeWidth="3.1"
      />
      <path
        d="M15 10.5C23.5 10.5 21.5 22 28.5 23.4M15 37.5C23.5 37.5 21.5 26 28.5 24.6"
        stroke="currentColor"
        strokeWidth="3.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M27.8 24H29.8"
        stroke="currentColor"
        strokeWidth="3.8"
        strokeLinecap="round"
      />
      <rect
        x="29.8"
        y="13.8"
        width="5"
        height="5"
        rx="1.1"
        fill="var(--seexr-text)"
      />
      <rect
        x="36.4"
        y="13.8"
        width="9.2"
        height="5"
        rx="1.1"
        fill="var(--seexr-text)"
      />
      <rect
        x="29.8"
        y="21.2"
        width="5"
        height="14.2"
        rx="1.2"
        fill="var(--seexr-text)"
      />
      <rect
        x="36.4"
        y="21.2"
        width="9.2"
        height="5.4"
        rx="1.1"
        fill="var(--seexr-text)"
      />
      <rect
        x="36.4"
        y="30"
        width="9.2"
        height="5.4"
        rx="1.1"
        fill="var(--seexr-text)"
      />
    </svg>
  );
}

function ObjectRiskAssessmentAgentIcon({
  className = "",
}: {
  className?: string;
}) {
  return (
    <svg
      className={className}
      width="24"
      height="24"
      viewBox="0 0 48 48"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="24" cy="24" r="13" stroke="currentColor" strokeWidth="3.2" />
      <circle cx="24" cy="24" r="4.5" fill="var(--seexr-text)" />
      <path
        d="M24 5V12M24 36V43M5 24H12M36 24H43"
        stroke="currentColor"
        strokeWidth="3.2"
        strokeLinecap="round"
      />
      <path
        d="M34.5 34.5L42 42"
        stroke="currentColor"
        strokeWidth="3.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function DemolitionRiskAssessmentAgentIcon({
  className = "",
}: {
  className?: string;
}) {
  return (
    <svg
      className={className}
      width="24"
      height="24"
      viewBox="0 0 48 48"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M8 13H27L36 21"
        stroke="currentColor"
        strokeWidth="3.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M29 15V28"
        stroke="currentColor"
        strokeWidth="3.4"
        strokeLinecap="round"
      />
      <path
        d="M29 28C29 31 32 31 32 28"
        stroke="currentColor"
        strokeWidth="3.4"
        strokeLinecap="round"
      />
      <path
        d="M7 42V25H22V42"
        stroke="currentColor"
        strokeWidth="3.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14 25L18 31L14 35L19 42"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect
        x="28"
        y="34"
        width="6"
        height="6"
        rx="1.3"
        fill="var(--seexr-text)"
      />
      <rect
        x="36"
        y="30"
        width="6"
        height="6"
        rx="1.3"
        fill="var(--seexr-text)"
      />
      <rect
        x="36"
        y="39"
        width="6"
        height="5"
        rx="1.2"
        fill="var(--seexr-text)"
      />
    </svg>
  );
}

function AccidentSearchAgentIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      className={className}
      width="24"
      height="24"
      viewBox="0 0 48 48"
      fill="none"
      aria-hidden="true"
    >
      <defs>
        <mask id="accident-search-warning-cutout" maskUnits="userSpaceOnUse">
          <rect width="48" height="48" fill="black" />
          <path d="M21 7L35 33H7L21 7Z" fill="white" />
          <circle cx="32" cy="32" r="8.8" fill="black" />
        </mask>
      </defs>
      <path
        d="M21 7L35 33H7L21 7Z"
        fill="var(--seexr-text)"
        mask="url(#accident-search-warning-cutout)"
      />
      <path
        d="M21 15.5V24.5"
        stroke="white"
        strokeWidth="3.1"
        strokeLinecap="round"
      />
      <circle cx="21" cy="28.9" r="1.8" fill="white" />
      <circle cx="32" cy="32" r="7" stroke="currentColor" strokeWidth="3.3" />
      <path
        d="M37 37L43 43"
        stroke="currentColor"
        strokeWidth="3.3"
        strokeLinecap="round"
      />
    </svg>
  );
}

function LegalSearchAgentIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      className={className}
      width="24"
      height="24"
      viewBox="0 0 48 48"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M10 7H29L36 14V30"
        stroke="var(--seexr-text)"
        strokeWidth="3.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M29 7V14H36"
        stroke="var(--seexr-text)"
        strokeWidth="3.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 7V41H27"
        stroke="var(--seexr-text)"
        strokeWidth="3.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect
        x="15"
        y="18"
        width="13"
        height="3.8"
        rx="1.2"
        fill="var(--seexr-text)"
      />
      <rect
        x="15"
        y="25"
        width="10"
        height="3.8"
        rx="1.2"
        fill="var(--seexr-text)"
      />
      <circle cx="32" cy="32" r="7" stroke="currentColor" strokeWidth="3.3" />
      <path
        d="M37 37L43 43"
        stroke="currentColor"
        strokeWidth="3.3"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ConceptualEstimateAgentIcon({
  className = "",
}: {
  className?: string;
}) {
  return (
    <svg
      className={className}
      width="24"
      height="24"
      viewBox="0 0 48 48"
      fill="none"
      aria-hidden="true"
    >
      <rect
        x="9"
        y="6"
        width="26"
        height="36"
        rx="4"
        stroke="currentColor"
        strokeWidth="3.4"
      />
      <rect
        x="14"
        y="12"
        width="16"
        height="7"
        rx="1.6"
        fill="var(--seexr-text)"
      />
      <rect
        x="14"
        y="24"
        width="5"
        height="5"
        rx="1.3"
        fill="var(--seexr-text)"
      />
      <rect
        x="23"
        y="24"
        width="5"
        height="5"
        rx="1.3"
        fill="var(--seexr-text)"
      />
      <rect
        x="14"
        y="33"
        width="5"
        height="5"
        rx="1.3"
        fill="var(--seexr-text)"
      />
      <path
        d="M38 12V21M33.5 16.5H42.5"
        stroke="currentColor"
        strokeWidth="3.2"
        strokeLinecap="round"
      />
      <path
        d="M31 35H42"
        stroke="currentColor"
        strokeWidth="3.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function getAgentHubIcon(agentId: string) {
  switch (agentId) {
    case "risk-assessment":
      return RiskAssessmentAgentIcon;
    case "object-risk-assessment":
      return ObjectRiskAssessmentAgentIcon;
    case "demolition-risk-assessment":
      return DemolitionRiskAssessmentAgentIcon;
    case "accident-search":
      return AccidentSearchAgentIcon;
    case "legal-search":
      return LegalSearchAgentIcon;
    case "conceptual-estimate":
      return ConceptualEstimateAgentIcon;
    default:
      return MessageCircle;
  }
}

export function Sidebar() {
  const isProductSurfaceLocked = useProductSurfaceLockActive();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const serializedSearchParams = searchParams.toString();
  const currentPath = `${pathname || "/"}${serializedSearchParams ? `?${serializedSearchParams}` : ""}`;
  const loginHref = buildLoginHref(currentPath);

  const [userProfile, setUserProfile] = useState<NormalizedUserProfile | null>(
    null,
  );
  const [sessions, setSessions] = useState<SidebarSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [sessionPage, setSessionPage] = useState(1);

  const [isCollapsed, setIsCollapsed] = useState(true);
  const [collapsedTooltip, setCollapsedTooltip] = useState<{
    label: string;
    top: number;
    left: number;
  } | null>(null);
  const [showFooterMenu, setShowFooterMenu] = useState(false);
  const [showDesktopLangMenu, setShowDesktopLangMenu] = useState(false);
  const [actionSessionId, setActionSessionId] = useState<string | null>(null);
  const [actionMenuPosition, setActionMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [hoveredSessionId, setHoveredSessionId] = useState<string | null>(null);
  const [isAgentHubExpanded, setIsAgentHubExpanded] = useState(true);
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [mobileActionOpen, setMobileActionOpen] = useState(false);

  // Add to Project
  const [projectPickerSessionId, setProjectPickerSessionId] = useState<
    string | null
  >(null);
  const [projectList, setProjectList] = useState<NormalizedProject[]>([]);
  const [projectListLoading, setProjectListLoading] = useState(false);

  const { theme, setTheme } = useTheme();
  const { t, language, setLanguage } = useLanguage();

  const popupRef = useRef<HTMLDivElement>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollTop, setCanScrollTop] = useState(false);
  const [canScrollBottom, setCanScrollBottom] = useState(false);

  const loadMoreRef = useRef<() => Promise<void>>(null);

  const updateScrollIndicators = () => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    setCanScrollTop(scrollTop > 0);
    setCanScrollBottom(Math.ceil(scrollTop + clientHeight) < scrollHeight - 2);
  };

  const handleScroll = () => {
    updateScrollIndicators();
    setActionSessionId(null);
    setActionMenuPosition(null);
    if (!scrollRef.current || loadingMore) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    if (scrollHeight - scrollTop - clientHeight < 100) {
      loadMoreRef.current?.();
    }
  };

  useEffect(() => {
    updateScrollIndicators();
    const handleResize = () => {
      updateScrollIndicators();
      setActionSessionId(null);
      setActionMenuPosition(null);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [sessions, projectList, showAllProjects, isCollapsed, isAgentHubExpanded]);

  // 사이드바 폭을 CSS variable로 document에 노출 — artifact 패널이 full-width 계산에 사용
  useEffect(() => {
    const width = isCollapsed ? "72px" : "260px";
    document.documentElement.style.setProperty(
      "--sidebar-width-desktop",
      width,
    );
  }, [isCollapsed]);

  const loadMoreSessions = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const nextPage = sessionPage + 1;
      const [result] = await Promise.all([
        fetchSidebarSessionPage(nextPage),
        new Promise((r) => setTimeout(r, 666)),
      ]);
      setSessions((prev) => mergeSidebarSessionFeed(prev, result.sessions));
      setSessionPage(nextPage);
      setHasMore(result.hasMore);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMore(false);
    }
  };

  loadMoreRef.current = loadMoreSessions;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent | TouchEvent) {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        setShowFooterMenu(false);
        setShowDesktopLangMenu(false);
      }
      if (!(event.target as Element).closest(".action-menu-container")) {
        setActionSessionId(null);
        setActionMenuPosition(null);
      }
      {
        const targetEl = event.target as Element;
        if (
          !targetEl.closest(".session-title-btn") &&
          !targetEl.closest(".session-action-menu")
        ) {
          setMobileActionOpen(false);
        }
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside, {
      passive: true,
    });
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    async function fetchSessionsOnly() {
      try {
        const result = await fetchSidebarSessionPage(1);
        setSessions(result.sessions);
        setSessionPage(1);
        setHasMore(result.hasMore);
      } catch (err) {
        console.error(err);
      }
    }

    async function fetchSidebarData() {
      try {
        const userRes = await fetchWithAuth(
          `${process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000"}/api/users/me`,
          { skipRedirectOn401: true },
        );
        if (userRes.ok) {
          const userData = await userRes.json();
          setUserProfile(normalizeUserProfile(userData));
        }
        await fetchSessionsOnly();
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    async function fetchProjectsOnly() {
      try {
        setProjectList(await fetchSidebarSiteProjects());
      } catch (err) {
        setProjectList([]);
        console.error(err);
      }
    }

    fetchSidebarData();
    fetchProjectsOnly();

    const onSessionCreated = () => {
      fetchSessionsOnly();
      fetchProjectsOnly();
    };
    const onResume = () => {
      fetchSidebarData();
      fetchProjectsOnly();
    };
    const onTitleUpdated = (e: Event) => {
      const { sessionId, title } = (
        e as CustomEvent<{ sessionId: string; title: string }>
      ).detail;
      setSessions((prev) =>
        prev.map((s) => (s.id === sessionId ? { ...s, title } : s)),
      );
    };
    window.addEventListener("session-created", onSessionCreated);
    window.addEventListener("session-title-updated", onTitleUpdated);
    window.addEventListener("projects-updated", fetchProjectsOnly);
    window.addEventListener("seexr:resume", onResume);
    return () => {
      window.removeEventListener("session-created", onSessionCreated);
      window.removeEventListener("session-title-updated", onTitleUpdated);
      window.removeEventListener("projects-updated", fetchProjectsOnly);
      window.removeEventListener("seexr:resume", onResume);
    };
  }, []);

  useEffect(() => {
    if (projectList.length <= SIDEBAR_PROJECT_PREVIEW_LIMIT) {
      setShowAllProjects(false);
    }
  }, [projectList.length]);

  const handleLogout = async () => {
    setShowFooterMenu(false);
    await fetchWithAuth(
      `${process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000"}/api/auth/logout`,
      { method: "POST" },
    );
    document.cookie = "seexr_access=; path=/; max-age=0";
    router.push("/login");
  };

  const updateSessionLocal = async (
    id: string,
    updates: SidebarSessionUpdate,
  ) => {
    try {
      const response = await fetchWithAuth(
        `${process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000"}/api/chat/sessions/${id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(
            toCoreSessionUpdate(updates as Record<string, unknown>),
          ),
        },
      );
      if (!response.ok) throw new Error(`status ${response.status}`);
      setSessions((prev) =>
        prev.map((s) => (s.id === id ? { ...s, ...updates } : s)),
      );
      if (Object.prototype.hasOwnProperty.call(updates, "project_id")) {
        window.dispatchEvent(new Event("projects-updated"));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const deleteSessionLocal = async (id: string) => {
    try {
      const response = await fetchWithAuth(
        `${process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000"}/api/chat/sessions/${id}`,
        {
          method: "DELETE",
        },
      );
      if (!response.ok) throw new Error(`status ${response.status}`);
      setSessions((prev) => prev.filter((s) => s.id !== id));
      window.dispatchEvent(new Event("projects-updated"));
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  };

  const openProjectPicker = async (sessionId: string) => {
    setProjectPickerSessionId(sessionId);
    setProjectListLoading(true);
    try {
      setProjectList(await fetchSidebarSiteProjects());
    } catch (e) {
      console.error(e);
    } finally {
      setProjectListLoading(false);
    }
  };

  useEffect(() => {
    const mainEl = document.getElementById("main-content");

    function handleBodyClick(e: MouseEvent | TouchEvent) {
      if (!isCollapsed && window.innerWidth < 768) {
        if (mainEl && mainEl.contains(e.target as Node)) {
          e.preventDefault();
          e.stopPropagation();
          setIsCollapsed(true);
        }
      }
    }

    const removeCaptureListeners = () => {
      document.removeEventListener("mousedown", handleBodyClick, true);
      document.removeEventListener("touchstart", handleBodyClick, true);
    };
    const clearMainStyles = () => {
      if (!mainEl) return;
      mainEl.style.transform = "";
      mainEl.style.borderRadius = "";
      mainEl.style.boxShadow = "";
    };
    const syncSidebarLayout = () => {
      removeCaptureListeners();

      if (!mainEl || window.innerWidth >= 768 || isCollapsed) {
        clearMainStyles();
        return;
      }

      mainEl.style.transform = "translateX(260px)";
      mainEl.style.borderRadius = "24px";
      mainEl.style.boxShadow =
        theme === "dark"
          ? "-10px 0 40px rgba(0,0,0,0.5)"
          : "-10px 0 40px rgba(0,0,0,0.15)";
      document.addEventListener("mousedown", handleBodyClick, true);
      document.addEventListener("touchstart", handleBodyClick, {
        capture: true,
        passive: false,
      });
    };

    syncSidebarLayout();
    window.addEventListener("resize", syncSidebarLayout);
    return () => {
      window.removeEventListener("resize", syncSidebarLayout);
      removeCaptureListeners();
      clearMainStyles();
    };
  }, [isCollapsed, theme]);

  const navItems = [
    { href: "/", label: t("nav.new_chat"), Icon: SquarePen },
    {
      href: CHAT_HISTORY_PATH,
      label: t("nav.chat_history"),
      Icon: MessageCircle,
    },
    { href: "/projects", label: t("nav.projects"), Icon: Folder },
    { href: "/artifacts", label: t("nav.artifacts"), Icon: LibraryBig },
  ];

  const starredSessions = sessions.filter((s) => s.is_starred);
  const regularSessions = sessions.filter((s) => !s.is_starred);
  // TEMPORARY PRODUCT LOCK: intentionally do not filter by agent_id. This
  // replacement feed combines generic chat and Agent Hub sessions.
  const unifiedRecentSessions = sessions.filter((s) => !s.is_starred);
  const visibleSidebarProjects = showAllProjects
    ? projectList
    : projectList.slice(0, SIDEBAR_PROJECT_PREVIEW_LIMIT);

  const activeSessionId = searchParams.get("session");

  // The header needs the exact active-session metadata even when that session
  // has fallen outside the first page of the recent-session feed. Keeping this
  // lookup here lets chat and Agent Hub workspaces share the same breadcrumb
  // and title-action behavior.
  useEffect(() => {
    if (!activeSessionId) return;

    let cancelled = false;
    void loadAgentSession(activeSessionId)
      .then((session) => {
        if (cancelled) return;
        setSessions((previous) => mergeSidebarSessionFeed(previous, [session]));
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [activeSessionId]);

  const toggleSessionActionMenu = (
    sessionId: string,
    trigger: HTMLButtonElement,
  ) => {
    if (actionSessionId === sessionId) {
      setActionSessionId(null);
      setActionMenuPosition(null);
      return;
    }

    const rect = trigger.getBoundingClientRect();
    const gap = 8;
    const menuWidth = 192;
    const menuHeight = 224;
    const preferredLeft = rect.right + gap;
    const left =
      preferredLeft + menuWidth <= window.innerWidth - gap
        ? preferredLeft
        : Math.max(gap, rect.left - menuWidth - gap);
    const top = Math.min(
      Math.max(gap, rect.top),
      Math.max(gap, window.innerHeight - menuHeight - gap),
    );

    setActionMenuPosition({ top, left });
    setActionSessionId(sessionId);
  };

  const renderSession = (session: SidebarSession) => {
    const isSessionActive = activeSessionId === session.id;
    return (
      <div
        key={session.id}
        onMouseEnter={() => setHoveredSessionId(session.id)}
        onMouseLeave={() =>
          setHoveredSessionId((current) =>
            current === session.id ? null : current,
          )
        }
        className={`group action-menu-container relative flex min-w-0 items-center rounded-lg transition-colors ${isSessionActive ? "seexr-sidebar-active" : "seexr-sidebar-hover"}`}
      >
        <Link
          href={getAgentSessionHref(session.agent_id, session.id)}
          onClick={() => {
            if (window.innerWidth <= 768) setIsCollapsed(true);
          }}
          className={`flex min-w-0 flex-1 items-center gap-1.5 truncate px-3 py-2 text-[13px] font-medium ${isSessionActive ? "text-seexr-primary" : "text-gray-500 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-100"}`}
        >
          <span className="truncate">{session.title || "대화 생성 중..."}</span>
        </Link>
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            toggleSessionActionMenu(session.id, event.currentTarget);
          }}
          onFocus={() => setHoveredSessionId(session.id)}
          onBlur={() => {
            if (actionSessionId !== session.id) setHoveredSessionId(null);
          }}
          className={`mr-1 shrink-0 cursor-pointer rounded-md p-1.5 text-gray-400 transition-[color,opacity,background-color] hover:bg-black/5 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-seexr-primary/30 dark:hover:bg-white/10 dark:hover:text-gray-100 ${actionSessionId === session.id || hoveredSessionId === session.id ? "pointer-events-auto opacity-100" : "pointer-events-auto opacity-100 md:pointer-events-none md:opacity-0"}`}
          aria-label={t("sidebar.menu.options")}
          aria-haspopup="menu"
          aria-expanded={actionSessionId === session.id}
        >
          <MoreVertical size={15} strokeWidth={2} />
        </button>
      </div>
    );
  };

  // Tooltip is portaled to the body so the collapsed sidebar's overflow-hidden
  // navigation cannot clip it. It is also shown for keyboard focus.
  const showCollapsedTooltip = (target: EventTarget | null) => {
    if (!isCollapsed || !(target instanceof Element)) return;
    const trigger = target.closest<HTMLElement>("[data-sidebar-tooltip]");
    const label = trigger?.dataset.sidebarTooltip;
    if (!trigger || !label) return;
    const rect = trigger.getBoundingClientRect();
    const next = { label, top: rect.top + rect.height / 2, left: rect.right + 10 };
    setCollapsedTooltip((current) =>
      current?.label === next.label &&
      current.top === next.top &&
      current.left === next.left
        ? current
        : next,
    );
  };

  const themeOptions = [
    { value: "system" as const, label: t("footer.system"), Icon: Monitor },
    { value: "light" as const, label: t("footer.light"), Icon: Sun },
    { value: "dark" as const, label: t("footer.dark"), Icon: Moon },
  ];

  let headerTitle = "SEEXR";
  const currentSession =
    activeSessionId &&
    (pathname === "/" ||
      pathname?.startsWith("/c/") ||
      pathname?.startsWith("/ah/"))
      ? (sessions.find((session) => session.id === activeSessionId) ?? null)
      : null;
  const currentProject = currentSession?.project_id
    ? (projectList.find(
        (project) => project.id === currentSession.project_id,
      ) ?? null)
    : null;

  const isRiskAssessmentWorkspace =
    pathname === "/ah/risk-assessment" ||
    pathname === "/ah/demolition-risk-assessment";
  const isObjectRiskAssessmentWorkspace =
    pathname === "/ah/object-risk-assessment";
  const isDedicatedRiskWorkspace =
    isRiskAssessmentWorkspace || isObjectRiskAssessmentWorkspace;

  if (isObjectRiskAssessmentWorkspace) {
    headerTitle = t("sidebar.object_risk_assessment") || "객체 중심 위험성평가";
  } else if (isRiskAssessmentWorkspace) {
    headerTitle = "AI 위험성평가";
  } else if (pathname === "/ah/accident-search") {
    headerTitle = t("sidebar.accident_search") || "사고 사례 검색";
  } else if (pathname === "/ah/legal-search") {
    headerTitle = t("sidebar.legal_search") || "법령 검색";
  } else if (pathname === CHAT_HISTORY_PATH) {
    headerTitle = t("nav.chat_history") || "Chat History";
  } else if (pathname === "/projects" || pathname?.startsWith("/projects/")) {
    headerTitle = t("nav.projects") || "Projects";
  } else if (pathname?.startsWith("/artifacts")) {
    headerTitle = t("nav.artifacts") || "Library";
  } else if (pathname === "/settings") {
    headerTitle = t("footer.settings") || "Settings";
  }

  // Session-bearing pages all share the same header contract:
  // project breadcrumb (when linked) + session title + title action menu.
  if (currentSession) {
    headerTitle = currentSession.title || t("chat.untitled") || "Chat Session";
  }

  // 데스크톱에서 헤더를 숨길 페이지 (채팅기록·프로젝트·아티팩트·설정·플랜·결제) — 모바일은 사이드바 토글을 위해 유지
  const hideHeaderOnDesktop =
    pathname === CHAT_HISTORY_PATH ||
    pathname === "/projects" ||
    pathname?.startsWith("/projects/") ||
    pathname?.startsWith("/artifacts") ||
    pathname === "/settings" ||
    pathname === "/plans" ||
    pathname?.startsWith("/payment/") ||
    pathname?.startsWith("/billing/");

  // 헤더와 사이드바 채팅 메뉴가 항상 같은 액션 구성을 사용한다.
  const renderSessionActionItems = (
    s: SidebarSession,
    closeMenu: () => void,
  ) => (
    <>
      <button
        type="button"
        onClick={() => {
          void updateSessionLocal(s.id, { is_starred: !s.is_starred });
          closeMenu();
        }}
        className="relative z-10 flex w-full cursor-pointer items-center gap-2 rounded-xl px-3 py-2.5 text-left text-[13px] font-semibold text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-[#2a2a2e]"
      >
        <Star
          size={15}
          strokeWidth={2}
          className={s.is_starred ? "fill-current" : ""}
        />
        {s.is_starred ? t("sidebar.menu.unstar") : t("sidebar.menu.star")}
      </button>
      <button
        type="button"
        onClick={() => {
          const newTitle = prompt(
            t("sidebar.menu.rename_prompt"),
            s.title ?? undefined,
          );
          if (newTitle && newTitle.trim() !== "" && newTitle !== s.title) {
            void updateSessionLocal(s.id, { title: newTitle.trim() });
          }
          closeMenu();
        }}
        className="relative z-10 flex w-full cursor-pointer items-center gap-2 rounded-xl px-3 py-2.5 text-left text-[13px] font-semibold text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-[#2a2a2e]"
      >
        <Pencil size={15} strokeWidth={2} />
        {t("sidebar.menu.rename")}
      </button>
      <button
        type="button"
        onClick={() => {
          void openProjectPicker(s.id);
          closeMenu();
        }}
        className="relative z-10 flex w-full cursor-pointer items-center gap-2 rounded-xl px-3 py-2.5 text-left text-[13px] font-semibold text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-[#2a2a2e]"
      >
        <FolderPlus size={15} strokeWidth={2} />
        {s.project_id
          ? t("sidebar.menu.change_project") || "프로젝트 변경"
          : t("sidebar.menu.add_project") || "프로젝트에 추가"}
      </button>
      {s.project_id && (
        <button
          type="button"
          onClick={() => {
            void updateSessionLocal(s.id, { project_id: null });
            closeMenu();
          }}
          className="relative z-10 flex w-full cursor-pointer items-center gap-2 rounded-xl px-3 py-2.5 text-left text-[13px] font-semibold text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-[#2a2a2e]"
        >
          <FolderMinus size={15} strokeWidth={2} />
          {t("sidebar.menu.remove_from_project") || "프로젝트에서 삭제"}
        </button>
      )}
      <div className="relative z-10 my-1 h-px bg-gray-200 dark:bg-[#2a2a2e]" />
      <button
        type="button"
        onClick={async () => {
          if (confirm(t("sidebar.menu.delete_confirm"))) {
            const deleted = await deleteSessionLocal(s.id);
            if (deleted && activeSessionId === s.id)
              router.push(CHAT_HISTORY_PATH);
          }
          closeMenu();
        }}
        className="relative z-10 flex w-full cursor-pointer items-center gap-2 rounded-xl px-3 py-2.5 text-left text-[13px] font-semibold text-red-600 transition-colors hover:bg-red-50 dark:hover:bg-red-950/30"
      >
        <Trash2 size={15} strokeWidth={2} />
        {t("sidebar.menu.delete")}
      </button>
    </>
  );

  const sessionActionItems = currentSession
    ? renderSessionActionItems(currentSession, () => setMobileActionOpen(false))
    : null;
  const actionMenuSession = actionSessionId
    ? (sessions.find((session) => session.id === actionSessionId) ?? null)
    : null;

  return (
    <>
      <style>{`
        :root {
          --seexr-sidebar-surface: color-mix(
            in oklab,
            #f7f7f7 97%,
            var(--seexr-sidebar-accent) 3%
          );
          --seexr-sidebar-hover: color-mix(
            in oklab,
            #eeeeee 95%,
            var(--seexr-sidebar-accent) 5%
          );
          --seexr-sidebar-active: color-mix(
            in oklab,
            #f1f1f1 90%,
            var(--seexr-sidebar-accent) 10%
          );
          --seexr-sidebar-border: color-mix(
            in oklab,
            #e5e5e5 94%,
            var(--seexr-sidebar-accent) 6%
          );
        }
        :root.dark {
          --seexr-sidebar-surface: color-mix(
            in oklab,
            #171717 97%,
            var(--seexr-sidebar-accent) 3%
          );
          --seexr-sidebar-hover: color-mix(
            in oklab,
            #262626 95%,
            var(--seexr-sidebar-accent) 5%
          );
          --seexr-sidebar-active: color-mix(
            in oklab,
            #242424 90%,
            var(--seexr-sidebar-accent) 10%
          );
          --seexr-sidebar-border: color-mix(
            in oklab,
            #2d2d2d 93%,
            var(--seexr-sidebar-accent) 7%
          );
        }
        .seexr-sidebar-surface {
          background-color: var(--seexr-sidebar-surface);
        }
        .seexr-sidebar-hover:hover,
        .seexr-sidebar-hover-active {
          background-color: var(--seexr-sidebar-hover);
        }
        .seexr-sidebar-active {
          background-color: var(--seexr-sidebar-active);
        }
        .seexr-sidebar-border {
          border-color: var(--seexr-sidebar-border);
        }
        .seexr-sidebar-fade-top {
          background: linear-gradient(
            to bottom,
            var(--seexr-sidebar-surface),
            transparent
          );
        }
        .seexr-sidebar-fade-bottom {
          background: linear-gradient(
            to top,
            var(--seexr-sidebar-surface),
            transparent
          );
        }
      `}</style>
      {/* Top Header (Fixed) — visible on mobile & desktop */}
      <div
        className={`fixed top-0 right-0 h-[60px] z-50 flex items-center transition-all duration-150 ease-out pointer-events-none left-0
          ${hideHeaderOnDesktop ? "md:hidden" : ""}
          ${
            isCollapsed
              ? "md:left-[72px]"
              : "translate-x-[260px] opacity-95 rounded-tl-[24px] md:translate-x-0 md:opacity-100 md:rounded-tl-none md:left-[260px]"
          }
        `}
      >
        {/* Extended Gradient overlay — 채팅 배경과 같은 색, 투명도만 변화 */}
        <div
          className={`absolute top-0 left-0 right-0 h-[110px] pointer-events-none bg-background transition-all duration-150 ease-out ${isCollapsed ? "" : "rounded-tl-[24px] md:rounded-tl-none"}`}
          style={{
            WebkitMaskImage:
              "linear-gradient(to bottom, black 45%, transparent 100%)",
            maskImage:
              "linear-gradient(to bottom, black 45%, transparent 100%)",
          }}
        />

        {/* Content row (left-aligned) */}
        <div className="relative z-10 flex items-center gap-2 w-full px-4 min-w-0">
          {/* Mobile-only sidebar toggle */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="md:hidden w-[40px] h-[40px] rounded-full p-2 flex items-center justify-center cursor-pointer transition-transform active:scale-90 shadow-sm border border-gray-200/50 dark:border-gray-700/50 bg-white/50 dark:bg-black/20 backdrop-blur-md shrink-0 pointer-events-auto"
            aria-label="Toggle Sidebar"
          >
            <img
              src="/images/seexr-light.png"
              alt="Seexr"
              className="w-full h-full object-contain drop-shadow-md"
            />
          </button>

          {/* LEFT cluster: project (all viewports) + desktop chevron + desktop title */}
          <div className="flex items-center gap-1.5 min-w-0 md:flex-1 pointer-events-auto">
            {currentProject && (
              <>
                <Link
                  href={`/projects/${currentProject.id}`}
                  className="text-[15px] md:text-[17px] font-bold tracking-tight text-gray-900 dark:text-gray-100 md:text-[#4B4947] md:dark:text-[#d0cec9] hover:text-seexr-primary md:hover:text-seexr-primary transition-colors truncate shrink min-w-0 max-md:max-w-[160px] md:max-w-[40%]"
                >
                  {currentProject.name}
                </Link>
                <ChevronRight
                  size={14}
                  strokeWidth={2.5}
                  className="shrink-0 text-gray-400 dark:text-gray-500 max-md:hidden"
                  aria-hidden
                />
              </>
            )}

            {/* DESKTOP title + dropdown (left-aligned) */}
            <div className="max-md:hidden relative min-w-0">
              <button
                onClick={(e) => {
                  if (currentSession) {
                    e.stopPropagation();
                    setMobileActionOpen(!mobileActionOpen);
                  }
                }}
                className="session-title-btn flex items-center gap-1.5 focus:outline-none max-w-full"
                disabled={!currentSession}
              >
                <h1 className="text-[17px] font-bold tracking-tight truncate text-left">
                  {headerTitle === "SEEXR" ? (
                    <Image
                      src="/images/seexr-logo-lettering.png"
                      alt="SEEXR"
                      width={180}
                      height={54}
                      className="h-[22px] w-auto object-contain"
                      priority
                    />
                  ) : (
                    headerTitle
                  )}
                </h1>
                {currentSession && (
                  <ChevronDown
                    size={16}
                    strokeWidth={2.5}
                    className={`text-gray-600 dark:text-gray-400 transition-transform shrink-0 ${mobileActionOpen ? "rotate-180" : ""}`}
                  />
                )}
              </button>

              {currentSession && mobileActionOpen && (
                <div className="session-action-menu absolute top-[calc(100%+14px)] left-0 w-48 bg-white dark:bg-[#1f1f22] rounded-2xl border border-gray-200 dark:border-[#2a2a2e] shadow-2xl flex flex-col p-1.5 animate-in fade-in zoom-in-95 duration-100">
                  <div className="absolute -top-2 left-5 w-4 h-4 bg-white dark:bg-[#1f1f22] border-t border-l border-gray-200 dark:border-[#2a2a2e] rotate-45" />
                  {sessionActionItems}
                </div>
              )}
            </div>
          </div>

          {/* MOBILE centered title + dropdown (absolute) */}
          {(currentSession || isDedicatedRiskWorkspace) && (
            <div className="md:hidden absolute left-1/2 -translate-x-1/2 max-w-[55%] pointer-events-auto">
              <div className="relative flex flex-col items-center">
                <button
                  onClick={(e) => {
                    if (!currentSession) return;
                    e.stopPropagation();
                    setMobileActionOpen(!mobileActionOpen);
                  }}
                  className="session-title-btn flex items-center gap-1.5 focus:outline-none max-w-full"
                  disabled={!currentSession}
                >
                  <h1 className="text-[15px] font-bold tracking-tight text-gray-900 dark:text-gray-100 truncate text-center">
                    {isDedicatedRiskWorkspace
                      ? headerTitle
                      : currentSession?.title ||
                        t("chat.untitled") ||
                        "Chat Session"}
                  </h1>
                  {currentSession && (
                    <ChevronDown
                      size={16}
                      strokeWidth={2.5}
                      className={`text-gray-600 dark:text-gray-400 transition-transform shrink-0 ${mobileActionOpen ? "rotate-180" : ""}`}
                    />
                  )}
                </button>

                {currentSession && mobileActionOpen && (
                  <div className="session-action-menu absolute top-[calc(100%+14px)] left-1/2 -translate-x-1/2 w-48 bg-white dark:bg-[#1f1f22] rounded-2xl border border-gray-200 dark:border-[#2a2a2e] shadow-2xl flex flex-col p-1.5 animate-in fade-in zoom-in-95 duration-100">
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white dark:bg-[#1f1f22] border-t border-l border-gray-200 dark:border-[#2a2a2e] rotate-45" />
                    {sessionActionItems}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {/* /content row */}
      </div>
      {/* /header root */}

      <aside
        onMouseOver={(event) => showCollapsedTooltip(event.target)}
        onMouseOut={(event) => {
          if (!isCollapsed) return;
          const trigger = event.target instanceof Element
            ? event.target.closest("[data-sidebar-tooltip]")
            : null;
          if (trigger && event.relatedTarget instanceof Node && trigger.contains(event.relatedTarget)) return;
          setCollapsedTooltip(null);
        }}
        onMouseLeave={() => setCollapsedTooltip(null)}
        onFocusCapture={(event) => showCollapsedTooltip(event.target)}
        onBlurCapture={() => setCollapsedTooltip(null)}
        onClickCapture={() => setCollapsedTooltip(null)}
        className={`seexr-sidebar-surface seexr-sidebar-border w-[260px] absolute inset-y-0 left-0 max-md:z-0 border-none ${isCollapsed ? "max-md:-translate-x-[100%] md:w-[72px]" : "max-md:translate-x-0 md:w-[260px]"} md:relative md:z-40 dark:md:border-r dark:md:border-solid flex-shrink-0 flex flex-col h-full transition-all duration-150 ease-out visible`}
      >
        {/* Sidebar Header */}
        <div
          className={`h-[60px] flex items-center ${isCollapsed ? "justify-center" : "justify-between px-4"} shrink-0 bg-transparent z-10 transition-all`}
        >
          {!isCollapsed && (
            <Link
              href={
                isProductSurfaceLocked
                  ? PRODUCT_SURFACE_LOCK_FALLBACK_PATH
                  : "/"
              }
              onClick={() => {
                if (window.innerWidth <= 768) setIsCollapsed(true);
              }}
              className="flex items-center gap-2 shrink-0"
            >
              <div className="max-md:hidden flex">
                <img
                  src="/images/seexr-light.png"
                  alt="Seexr Logo"
                  className="h-7 w-auto object-contain dark:hidden"
                />
                <img
                  src="/images/seexr-dark.png"
                  alt="Seexr Logo"
                  className="h-7 w-auto object-contain hidden dark:block"
                />
              </div>
              <span className="md:hidden font-bold text-[20px] tracking-tight text-[#4B4947] dark:text-[#d0cec9]">
                Seexr
              </span>
            </Link>
          )}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            data-sidebar-tooltip={t("sidebar.expand")}
            className="seexr-sidebar-hover max-md:hidden p-1.5 cursor-pointer text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 rounded-md transition-colors"
          >
            <PanelLeft size={18} strokeWidth={1.75} />
          </button>
        </div>

        {/* Sidebar Body */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Navigation (Fixed) */}
          <div
            className={`p-4 shrink-0 transition-all ${isCollapsed ? "" : "pb-0"}`}
          >
            <nav className="flex flex-col gap-1.5">
              {isProductSurfaceLocked && (
                <Link
                  href="/projects"
                  onClick={() => {
                    if (window.innerWidth <= 768) setIsCollapsed(true);
                  }}
                  data-sidebar-tooltip="현장 프로젝트"
                  className={`flex items-center w-full gap-3 px-3 py-2 rounded-[12px] text-[13px] font-medium transition-colors duration-100 overflow-hidden whitespace-nowrap ${
                    pathname === "/projects" ||
                    pathname?.startsWith("/projects/")
                      ? "seexr-sidebar-active text-seexr-primary"
                      : "seexr-sidebar-hover text-gray-600 dark:text-gray-400 hover:shadow-sm hover:text-gray-900 dark:hover:text-gray-100"
                  }`}
                >
                  <Folder size={18} strokeWidth={1.75} className="shrink-0" />
                  <span
                    className={`min-w-0 truncate transition-opacity duration-150 ${isCollapsed ? "opacity-0" : "opacity-100"}`}
                  >
                    현장 프로젝트
                  </span>
                </Link>
              )}
              {isProductSurfaceLocked && (
                <Link
                  href="/artifacts"
                  onClick={() => {
                    if (window.innerWidth <= 768) setIsCollapsed(true);
                  }}
                  data-sidebar-tooltip={t("nav.artifacts")}
                  className={[
                    "flex items-center w-full gap-3 px-3 py-2 rounded-[12px] text-[13px] font-medium transition-colors duration-100 overflow-hidden whitespace-nowrap",
                    pathname === "/artifacts" || pathname?.startsWith("/artifacts/")
                      ? "seexr-sidebar-active text-seexr-primary"
                      : "seexr-sidebar-hover text-gray-600 dark:text-gray-400 hover:shadow-sm hover:text-gray-900 dark:hover:text-gray-100",
                  ].join(" ")}
                >
                  <LibraryBig size={18} strokeWidth={1.75} className="shrink-0" />
                  <span
                    className={[
                      "min-w-0 truncate transition-opacity duration-150",
                      isCollapsed ? "opacity-0" : "opacity-100",
                    ].join(" ")}
                  >
                    {t("nav.artifacts")}
                  </span>
                </Link>
              )}
              {isProductSurfaceLocked && (
                <div
                  aria-hidden={isCollapsed}
                  className={[
                    "mt-3 whitespace-nowrap px-3 pb-0.5 text-[11px] font-black uppercase tracking-widest text-gray-400",
                    isCollapsed ? "invisible" : "",
                  ].join(" ")}
                >
                  {t("sidebar.agents_hub")}
                </div>
              )}
              {isProductSurfaceLocked
                ? /* TEMPORARY PRODUCT LOCK: Agent Hub entries replace the legacy
                 primary navigation. Keep the legacy branch below intact. */
                  AGENT_HUB_ITEMS.map((agent) => {
                    const isAgentActive = pathname === agent.href;
                    const AgentIcon = getAgentHubIcon(agent.id);
                    const label = t(agent.labelKey);
                    if (agent.requiresProductUnlock) {
                      return (
                        <div
                          key={agent.href}
                          role="link"
                          aria-disabled="true"
                          aria-label={`${label}: 준비중`}
                          title={isCollapsed ? undefined : "준비중"}
                          data-sidebar-tooltip={label}
                          className="flex w-full cursor-not-allowed select-none items-center gap-3 overflow-hidden whitespace-nowrap rounded-[12px] px-3 py-2 text-[13px] font-medium text-gray-300 opacity-70 dark:text-gray-600"
                        >
                          <AgentIcon className="h-[18px] w-[18px] shrink-0" />
                          <span
                            className={`min-w-0 truncate transition-opacity duration-150 ${isCollapsed ? "opacity-0" : "opacity-100"}`}
                          >
                            {label}
                          </span>
                          {agent.beta && !isCollapsed && (
                            <span className="ml-auto shrink-0 rounded-full border border-gray-200 bg-gray-100 px-1.5 py-0.5 text-[9px] font-black uppercase leading-none tracking-wide text-gray-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-500">
                              {t("sidebar.beta")}
                            </span>
                          )}
                        </div>
                      );
                    }
                    return (
                      <Link
                        key={agent.href}
                        href={agent.href}
                        onClick={() => {
                          if (window.innerWidth <= 768) setIsCollapsed(true);
                        }}
                        data-sidebar-tooltip={label}
                        className={`flex items-center w-full gap-3 px-3 py-2 rounded-[12px] text-[13px] font-medium transition-colors duration-100 overflow-hidden whitespace-nowrap ${
                          isAgentActive
                            ? "seexr-sidebar-active text-seexr-primary"
                            : "seexr-sidebar-hover text-gray-600 dark:text-gray-400 hover:shadow-sm hover:text-gray-900 dark:hover:text-gray-100"
                        }`}
                      >
                        <AgentIcon className="h-[18px] w-[18px] shrink-0" />
                        <span
                          className={`min-w-0 truncate transition-opacity duration-150 ${isCollapsed ? "opacity-0" : "opacity-100"}`}
                        >
                          {label}
                        </span>
                        {agent.beta && !isCollapsed && (
                          <span className="ml-auto shrink-0 rounded-full border border-seexr-200/60 bg-seexr-50/50 px-1.5 py-0.5 text-[9px] font-black uppercase leading-none tracking-wide text-seexr-primary backdrop-blur-md dark:border-seexr-400/20 dark:bg-seexr-400/10 dark:text-seexr-primary">
                            {t("sidebar.beta")}
                          </span>
                        )}
                      </Link>
                    );
                  })
                : /* PRESERVED FOR REACTIVATION: New Chat, Chat History, Projects,
                 and Artifacts return when the central lock is disabled. */
                  navItems.map((item) => {
                    const requiresExactMatch =
                      item.href === "/" || item.href === "/projects";
                    const isActive = requiresExactMatch
                      ? pathname === item.href
                      : pathname === item.href ||
                        (item.href !== CHAT_HISTORY_PATH &&
                          pathname?.startsWith(`${item.href}/`));
                    return (
                      <Link
                        key={item.href}
                        href={item.href}
                        onClick={() => {
                          if (window.innerWidth <= 768) setIsCollapsed(true);
                        }}
                        data-sidebar-tooltip={item.label}
                        className={`flex items-center w-full gap-3 px-3 py-2 rounded-[12px] text-[13px] font-medium transition-colors duration-100 overflow-hidden whitespace-nowrap ${
                          isActive
                            ? "seexr-sidebar-active text-seexr-primary"
                            : "seexr-sidebar-hover text-gray-600 dark:text-gray-400 hover:shadow-sm hover:text-gray-900 dark:hover:text-gray-100"
                        }`}
                      >
                        <item.Icon
                          size={18}
                          strokeWidth={1.75}
                          className="shrink-0"
                        />
                        <span
                          className={`transition-opacity duration-150 ${isCollapsed ? "opacity-0" : "opacity-100"}`}
                        >
                          {item.label}
                        </span>
                      </Link>
                    );
                  })}
            </nav>
          </div>

          {/* Scrollable Dynamic Content */}
          {!isCollapsed && (
            <div className="flex-1 relative flex flex-col overflow-hidden">
              {/* Top Indicator */}
              <div
                className={`seexr-sidebar-fade-top seexr-sidebar-border absolute top-0 left-0 right-0 h-6 z-10 pointer-events-none transition-opacity duration-150 border-t opacity-0 ${canScrollTop ? "md:opacity-100" : ""}`}
                style={{ opacity: canScrollTop ? 1 : 0 }}
              />

              <div
                ref={scrollRef}
                onScroll={handleScroll}
                className="flex-1 overflow-y-auto overflow-x-hidden px-4 pt-4 pb-4 flex flex-col gap-6 custom-scrollbar pr-2"
              >
                {/* Agents List */}
                {!isProductSurfaceLocked && (
                  <div className="flex flex-col">
                    <button
                      onClick={() => setIsAgentHubExpanded(!isAgentHubExpanded)}
                      className="px-3 flex items-center justify-between text-[11px] font-black text-gray-400 uppercase tracking-widest mb-1 hover:text-gray-600 dark:hover:text-gray-300 transition-colors w-full cursor-pointer group"
                    >
                      <span>{t("sidebar.agents_hub")}</span>
                      <ChevronDown
                        size={12}
                        strokeWidth={2.5}
                        className={`transition-transform duration-150 text-gray-300 dark:text-gray-600 group-hover:text-gray-400 dark:group-hover:text-gray-500 ${isAgentHubExpanded ? "" : "-rotate-90"}`}
                      />
                    </button>

                    {isAgentHubExpanded && (
                      <div className="flex flex-col gap-1 my-0.5 pb-1">
                        {AGENT_HUB_ITEMS.map((agent) => {
                          const isAgentActive =
                            pathname === agent.href &&
                            !searchParams.get("session");
                          const AgentIcon = getAgentHubIcon(agent.id);
                          const label = t(agent.labelKey);
                          return (
                            <Link
                              key={agent.href}
                              href={agent.href}
                              onClick={() => {
                                if (window.innerWidth <= 768)
                                  setIsCollapsed(true);
                              }}
                              className={`relative flex min-w-0 items-center gap-3 px-3 py-2 text-[13px] font-medium rounded-[12px] transition-colors duration-100 group overflow-hidden whitespace-nowrap ${
                                isAgentActive
                                  ? "seexr-sidebar-active text-seexr-primary"
                                  : "seexr-sidebar-hover text-gray-600 dark:text-gray-400 hover:shadow-sm hover:text-gray-900 dark:hover:text-gray-100"
                              }`}
                            >
                              <AgentIcon className="h-[18px] w-[18px] shrink-0" />
                              <span className="truncate">{label}</span>
                              {agent.beta && (
                                <span className="ml-2 shrink-0 rounded-full border border-seexr-200/60 bg-seexr-50/50 px-1.5 py-0.5 text-[9px] font-black uppercase leading-none tracking-wide text-seexr-primary backdrop-blur-md dark:border-seexr-400/20 dark:bg-seexr-400/10 dark:text-seexr-primary">
                                  {t("sidebar.beta")}
                                </span>
                              )}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Projects */}
                {projectList.length > 0 && (
                  <div className="flex flex-col gap-1">
                    <div className="px-3 flex items-center justify-between text-[11px] font-black text-gray-400 uppercase tracking-widest mb-1">
                      {t("nav.projects")}
                    </div>
                    <div className="flex flex-col gap-0.5">
                      {visibleSidebarProjects.map((project) => {
                        const projectHref = `/projects/${project.id}`;
                        const isProjectActive =
                          pathname === projectHref ||
                          pathname?.startsWith(`${projectHref}/`);
                        return (
                          <Link
                            key={project.id}
                            href={projectHref}
                            onClick={() => {
                              if (window.innerWidth <= 768)
                                setIsCollapsed(true);
                            }}
                            className={`flex min-w-0 items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium transition-colors ${
                              isProjectActive
                                ? "seexr-sidebar-active text-seexr-primary"
                                : "seexr-sidebar-hover text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
                            }`}
                          >
                            <span
                              className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md ${
                                isProjectActive
                                  ? "bg-seexr-solid/10 text-seexr-primary"
                                  : "bg-gray-200/70 text-gray-500 dark:bg-gray-700 dark:text-gray-300"
                              }`}
                            >
                              <Building2 size={12} strokeWidth={2.2} />
                            </span>
                            <span className="truncate">{project.name}</span>
                          </Link>
                        );
                      })}
                      {projectList.length > SIDEBAR_PROJECT_PREVIEW_LIMIT && (
                        <button
                          type="button"
                          onClick={() =>
                            setShowAllProjects((current) => !current)
                          }
                          className="seexr-sidebar-hover flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[12px] font-semibold text-gray-400 transition-colors hover:text-gray-700 dark:hover:text-gray-200"
                          aria-expanded={showAllProjects}
                        >
                          {showAllProjects ? (
                            <ChevronUp
                              size={15}
                              strokeWidth={2}
                              className="ml-0.5 shrink-0"
                            />
                          ) : (
                            <ChevronDown
                              size={15}
                              strokeWidth={2}
                              className="ml-0.5 shrink-0"
                            />
                          )}
                          <span>
                            {showAllProjects
                              ? t("sidebar.project_show_less")
                              : t("sidebar.project_show_more")}
                          </span>
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Starred Chats */}
                {starredSessions.length > 0 && (
                  <div className="flex flex-col gap-1 mb-4">
                    <div className="px-3 flex items-center justify-between text-[11px] font-black text-gray-400 uppercase tracking-widest mb-1">
                      {t("sidebar.starred")}
                    </div>
                    <div className="flex flex-col gap-0.5">
                      {starredSessions.map(renderSession)}
                    </div>
                  </div>
                )}

                {isProductSurfaceLocked ? (
                  /* TEMPORARY PRODUCT LOCK: replacement copy of Recent Chats.
                   It deliberately renders chat and agent sessions together. */
                  <div className="flex flex-col gap-1 flex-1">
                    <div className="px-3 flex items-center justify-between text-[11px] font-black text-gray-400 uppercase tracking-widest mb-1">
                      {t("sidebar.recent_sessions")}
                    </div>
                    <div className="flex flex-col gap-0.5">
                      {!loading && unifiedRecentSessions.length === 0 && (
                        <span className="px-3 py-2 text-[12px] text-gray-400 font-medium italic">
                          {t("sidebar.no_recent_sessions")}
                        </span>
                      )}
                      {loading && (
                        <div
                          className="flex items-center justify-center py-3"
                          aria-label={t("sidebar.loading")}
                        >
                          <div className="w-4 h-4 border-2 border-seexr-primary/30 border-t-seexr-primary rounded-full animate-spin" />
                        </div>
                      )}
                      {unifiedRecentSessions.map(renderSession)}
                      {loadingMore && (
                        <div
                          className="flex items-center justify-center py-3"
                          aria-label={t("sidebar.loading")}
                        >
                          <div className="w-4 h-4 border-2 border-seexr-primary/30 border-t-seexr-primary rounded-full animate-spin" />
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  /* PRESERVED FOR REACTIVATION: original Recent Chats section. */
                  <div className="flex flex-col gap-1 flex-1">
                    <div className="px-3 flex items-center justify-between text-[11px] font-black text-gray-400 uppercase tracking-widest mb-1">
                      {t("sidebar.recent_chats")}
                    </div>
                    <div className="flex flex-col gap-0.5">
                      {!loading && regularSessions.length === 0 && (
                        <span className="px-3 py-2 text-[12px] text-gray-400 font-medium italic">
                          {t("sidebar.no_recent_chats")}
                        </span>
                      )}
                      {loading && (
                        <div
                          className="flex items-center justify-center py-3"
                          aria-label={t("sidebar.loading")}
                        >
                          <div className="w-4 h-4 border-2 border-seexr-primary/30 border-t-seexr-primary rounded-full animate-spin" />
                        </div>
                      )}
                      {regularSessions.map(renderSession)}
                      {loadingMore && (
                        <div
                          className="flex items-center justify-center py-3"
                          aria-label={t("sidebar.loading")}
                        >
                          <div className="w-4 h-4 border-2 border-seexr-primary/30 border-t-seexr-primary rounded-full animate-spin" />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Bottom Indicator */}
              <div
                className={`seexr-sidebar-fade-bottom seexr-sidebar-border absolute bottom-0 left-0 right-0 h-6 z-10 pointer-events-none transition-opacity duration-150 border-b opacity-0 ${canScrollBottom ? "md:opacity-100" : ""}`}
                style={{ opacity: canScrollBottom ? 1 : 0 }}
              />
            </div>
          )}
        </div>

        {/* Sidebar Footer */}
        <div
          className="relative p-4 shrink-0 bg-transparent mt-auto"
          ref={popupRef}
        >
          {/* Footer Popup */}
          {showFooterMenu && (
            <div className="seexr-sidebar-border absolute bottom-[calc(100%+8px)] left-3 w-[240px] bg-background rounded-2xl border shadow-[0_4px_30px_rgba(0,0,0,0.15)] flex flex-col z-50 origin-bottom-left animate-in fade-in zoom-in-95 duration-100">
              <div className="px-4 pb-2 pt-3">
                {userProfile ? (
                  <span className="block truncate text-[13px] font-black text-foreground">
                    {userProfile.email}
                  </span>
                ) : (
                  <span className="text-[13px] font-bold text-gray-500 dark:text-gray-400">
                    로그인을 하고 더 많은 혜택을 누려보세요!
                  </span>
                )}
              </div>
              <div className="flex flex-col text-[13px] font-medium text-gray-600 dark:text-gray-400">
                {/* Theme Toggle */}
                <div className="px-2 pb-2">
                  <div className="flex items-center gap-1 p-1 rounded-lg bg-gray-100 dark:bg-gray-800">
                    {themeOptions.map(({ value, label, Icon: ThemeIcon }) => (
                      <button
                        key={value}
                        type="button"
                        aria-pressed={theme === value}
                        onClick={() => setTheme(value)}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md cursor-pointer text-[12px] font-semibold transition-all ${
                          theme === value
                            ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm"
                            : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                        }`}
                      >
                        <ThemeIcon size={13} strokeWidth={2} />
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="seexr-sidebar-border flex flex-col gap-1 border-t p-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowFooterMenu(false);
                      if (userProfile) {
                        openSettings("general");
                      } else {
                        router.push(loginHref);
                      }
                      if (window.innerWidth <= 768) setIsCollapsed(true);
                    }}
                    className="seexr-sidebar-hover flex w-full items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:text-foreground transition-colors text-left"
                  >
                    <Settings size={15} strokeWidth={2} />
                    {t("footer.settings")}
                  </button>

                  {/* Language Selector */}
                  {/* Mobile (native select) */}
                  <div className="md:hidden relative w-full">
                    <button className="seexr-sidebar-hover flex items-center justify-between w-full gap-3 px-3 py-2 rounded-lg cursor-pointer hover:text-foreground transition-colors text-left text-[13px] font-medium text-gray-600 dark:text-gray-400">
                      <div className="flex items-center gap-3">
                        <Globe size={15} strokeWidth={2} />
                        <span>{t("footer.language")}</span>
                      </div>
                      <ChevronDown
                        size={14}
                        strokeWidth={2.5}
                        className="opacity-50"
                      />
                    </button>
                    <select
                      value={language}
                      onChange={(e) =>
                        setLanguage(e.target.value as typeof language)
                      }
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer appearance-none"
                    >
                      {LANGUAGE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Desktop (popover menu) w/ Click Toggle */}
                  <div className="hidden md:flex relative w-full">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDesktopLangMenu(!showDesktopLangMenu);
                      }}
                      className={`flex items-center justify-between w-full gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors text-left text-[13px] font-medium ${showDesktopLangMenu ? "seexr-sidebar-hover-active text-foreground" : "seexr-sidebar-hover text-gray-600 dark:text-gray-400 hover:text-foreground"}`}
                    >
                      <div className="flex items-center gap-3">
                        <Globe size={15} strokeWidth={2} />
                        <span>{t("footer.language")}</span>
                      </div>
                      <ChevronRight
                        size={14}
                        strokeWidth={2.5}
                        className={`opacity-50 transition-transform ${showDesktopLangMenu ? "rotate-90" : "rotate-0"}`}
                      />
                    </button>

                    {showDesktopLangMenu && (
                      <div className="seexr-sidebar-border absolute left-[calc(100%+8px)] bottom-0 w-[160px] bg-background rounded-xl border shadow-[0_4px_30px_rgba(0,0,0,0.15)] p-1 z-[60] origin-bottom-left animate-in fade-in zoom-in-95 duration-100 flex flex-col">
                        {LANGUAGE_OPTIONS.map((opt) => (
                          <button
                            key={opt.value}
                            onClick={(e) => {
                              e.stopPropagation();
                              setLanguage(opt.value);
                              setShowDesktopLangMenu(false);
                            }}
                            className={`flex w-full px-3 py-2 text-[13px] font-medium rounded-lg cursor-pointer transition-colors text-left ${
                              language === opt.value
                                ? "seexr-sidebar-active text-seexr-primary shadow-[inset_0_0_0_1px_var(--seexr-sidebar-border)]"
                                : "seexr-sidebar-hover text-gray-600 dark:text-gray-400 hover:text-foreground"
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setShowFooterMenu(false);
                      openPlanCatalog();
                    }}
                    className="seexr-sidebar-hover flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:text-seexr-primary transition-colors text-left"
                  >
                    <Zap size={15} strokeWidth={2} />
                    <span>{t("footer.upgrade_plan")}</span>
                  </button>
                  <button className="seexr-sidebar-hover flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:text-foreground transition-colors text-left">
                    <MessageSquare size={15} strokeWidth={2} />
                    <span>{t("footer.send_feedback")}</span>
                  </button>
                </div>
              </div>
              <div className="seexr-sidebar-border border-t p-2 shrink-0">
                {userProfile ? (
                  <button
                    onClick={handleLogout}
                    className="flex w-full items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-red-50 dark:hover:bg-red-950/30 text-[13px] font-medium text-red-600 transition-colors text-left"
                  >
                    <LogOut size={15} strokeWidth={2} />
                    {t("footer.logout")}
                  </button>
                ) : (
                  <Link
                    href={loginHref}
                    onClick={() => {
                      setShowFooterMenu(false);
                      if (window.innerWidth <= 768) setIsCollapsed(true);
                    }}
                    className="seexr-sidebar-hover flex w-full items-center gap-3 px-3 py-2 rounded-lg cursor-pointer text-seexr-primary font-bold transition-colors text-left"
                  >
                    {t("footer.logout").replace(
                      /log\s*out|로그아웃|登出|Đăng xuất|Гарах/i,
                      (m: string) => {
                        const map: Record<string, string> = {
                          로그아웃: "로그인",
                          登出: "登入",
                          "Đăng xuất": "Đăng nhập",
                          Гарах: "Нэвтрэх",
                        };
                        return map[m] || "Log in";
                      },
                    )}
                  </Link>
                )}
              </div>
            </div>
          )}

          {/* Footer Trigger */}
          <button
            onClick={() => {
              setShowFooterMenu(!showFooterMenu);
            }}
            data-sidebar-tooltip={userProfile ? t("footer.manage_account") : t("auth.title")}
            className={`seexr-sidebar-hover flex w-full items-center gap-3 pl-[5px] rounded-[14px] cursor-pointer transition-colors overflow-hidden whitespace-nowrap`}
          >
            {loading ? (
              <>
                <div className="seexr-sidebar-surface seexr-sidebar-border w-8 h-8 rounded-full shrink-0 animate-pulse border"></div>
                <div
                  className={`flex flex-col overflow-hidden leading-tight flex-1 transition-opacity duration-150 ${isCollapsed ? "opacity-0" : "opacity-100"}`}
                >
                  <span className="text-[13px] font-black text-foreground truncate text-left">
                    {t("sidebar.loading")}
                  </span>
                </div>
              </>
            ) : userProfile ? (
              <>
                <UserAvatar
                  avatarFileId={userProfile.avatar_file_id}
                  username={userProfile.username}
                  className="w-8 h-8 text-sm"
                />
                <div
                  className={`flex flex-col overflow-hidden leading-tight flex-1 transition-opacity duration-150 ${isCollapsed ? "opacity-0" : "opacity-100"}`}
                >
                  <span className="text-[13px] font-black text-foreground truncate text-left">
                    {userProfile?.username}
                  </span>
                  <span className="text-[11px] font-semibold text-seexr-primary truncate uppercase tracking-widest mt-0.5 text-left">
                    {userProfile?.role || t("footer.free_plan")}
                  </span>
                </div>
              </>
            ) : (
              <>
                {isCollapsed ? (
                  <div className="seexr-sidebar-surface seexr-sidebar-border w-8 h-8 rounded-full shrink-0 flex items-center justify-center border text-gray-500 dark:text-gray-400">
                    <User size={18} strokeWidth={2} />
                  </div>
                ) : (
                  <div className="flex-1 transition-opacity duration-150 opacity-100 pr-1 flex items-center">
                    <div
                      role="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        router.push(loginHref);
                      }}
                      className="w-full h-[38px] flex items-center justify-center border border-gray-300 dark:border-white/20 rounded-full bg-white dark:bg-transparent hover:bg-gray-50 dark:hover:bg-white/5 transition-colors font-semibold text-[14px] text-gray-700 dark:text-gray-200 cursor-pointer"
                    >
                      로그인
                    </div>
                  </div>
                )}
              </>
            )}
            <ChevronUp
              size={16}
              strokeWidth={2}
              className={`text-gray-400 transition-all duration-200 shrink-0 ${showFooterMenu ? "rotate-180" : ""} ${isCollapsed ? "opacity-0" : "opacity-100"}`}
            />
          </button>
        </div>
      </aside>

      {/* Collapsed-nav tooltip floats outside the clipped Sidebar body. */}
      {isCollapsed && collapsedTooltip && typeof document !== "undefined" &&
        createPortal(
          <div
            role="tooltip"
            className="pointer-events-none fixed z-[10050] -translate-y-1/2 whitespace-nowrap rounded-full bg-[#101012] px-3 py-2 text-[12px] font-semibold leading-none text-white shadow-[0_4px_14px_rgba(0,0,0,0.18)]"
            style={{ top: collapsedTooltip.top, left: collapsedTooltip.left }}
          >
            {/* Wider-angle triangle, with its base buried inside the pill so no corners ("ears") protrude. */}
            <span
              aria-hidden="true"
              className="absolute -left-[6px] top-1/2 h-[22px] w-[14px] -translate-y-1/2 bg-[#101012] [clip-path:polygon(0_50%,100%_0,100%_100%)]"
            />
            {collapsedTooltip.label}
          </div>,
          document.body,
        )}

      {/* Sidebar chat action menu — portal로 렌더링해 스크롤 컨테이너에 잘리지 않게 한다. */}
      {typeof document !== "undefined" &&
        actionMenuSession &&
        actionMenuPosition &&
        createPortal(
          <div
            className="action-menu-container fixed z-[10000] flex w-48 origin-left flex-col rounded-2xl border border-gray-200 bg-white p-1.5 shadow-2xl animate-in fade-in zoom-in-95 duration-100 dark:border-[#2a2a2e] dark:bg-[#1f1f22]"
            style={{
              top: actionMenuPosition.top,
              left: actionMenuPosition.left,
            }}
            aria-label={t("sidebar.menu.options")}
          >
            {renderSessionActionItems(actionMenuSession, () => {
              setActionSessionId(null);
              setActionMenuPosition(null);
            })}
          </div>,
          document.body,
        )}

      {/* Project Picker Modal */}
      {projectPickerSessionId && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setProjectPickerSessionId(null)}
        >
          <div
            className="w-full max-w-sm mx-4 bg-white dark:bg-[#1a1a1c] rounded-2xl border border-gray-200 dark:border-[#2a2a2e] shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 pt-5 pb-3">
              <h3 className="text-[16px] font-black text-gray-900 dark:text-gray-100">
                {t("sidebar.menu.add_project")}
              </h3>
              <p className="text-[13px] text-gray-500 dark:text-gray-400 font-medium mt-1">
                {t("chat.project_modal_single_subtitle")}
              </p>
            </div>
            <div className="max-h-[280px] overflow-y-auto px-3 pb-2">
              {projectListLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-seexr-primary border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : projectList.length === 0 ? (
                <div className="flex flex-col items-center py-8 text-center px-4">
                  <Folder
                    size={20}
                    strokeWidth={2}
                    className="text-gray-400 mb-2"
                  />
                  <span className="text-[13px] font-medium text-gray-500 dark:text-gray-400">
                    {t("chat.project_modal_no_projects")}
                  </span>
                </div>
              ) : (
                <div className="flex flex-col gap-1">
                  {/* Unlink option */}
                  <button
                    onClick={async () => {
                      await updateSessionLocal(projectPickerSessionId, {
                        project_id: null,
                      });
                      setProjectPickerSessionId(null);
                    }}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-left hover:bg-gray-100 dark:hover:bg-[#2a2a2e] transition-colors"
                  >
                    <div className="w-9 h-9 rounded-xl bg-gray-100 dark:bg-[#1f1f22] flex items-center justify-center border border-gray-200 dark:border-[#2a2a2e]">
                      <X size={14} strokeWidth={2} className="text-gray-400" />
                    </div>
                    <span className="text-[13px] font-semibold text-gray-500 dark:text-gray-400">
                      {t("chat.project_modal_none")}
                    </span>
                  </button>
                  {projectList.map((project) => {
                    const currentSession = sessions.find(
                      (s) => s.id === projectPickerSessionId,
                    );
                    const isLinked = currentSession?.project_id === project.id;
                    return (
                      <button
                        key={project.id}
                        onClick={async () => {
                          await updateSessionLocal(projectPickerSessionId, {
                            project_id: project.id,
                          });
                          setProjectPickerSessionId(null);
                        }}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${isLinked ? "bg-seexr-solid/10 ring-1 ring-seexr-primary/30" : "hover:bg-gray-100 dark:hover:bg-[#2a2a2e]"}`}
                      >
                        <span
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border ${isLinked ? "bg-seexr-solid/10 text-seexr-primary border-seexr-primary/20" : "bg-gray-100 dark:bg-[#1f1f22] text-gray-600 dark:text-gray-400 border-gray-200 dark:border-[#2a2a2e]"}`}
                        >
                          <Building2 size={17} strokeWidth={2.1} />
                        </span>
                        <div className="flex flex-col min-w-0">
                          <span
                            className={`text-[13px] font-bold truncate ${isLinked ? "text-seexr-primary" : "text-gray-800 dark:text-gray-200"}`}
                          >
                            {project.name}
                          </span>
                          {project.description && (
                            <span className="text-[11px] text-gray-400 truncate">
                              {project.description}
                            </span>
                          )}
                        </div>
                        {isLinked && (
                          <Check
                            size={16}
                            strokeWidth={3}
                            className="text-seexr-primary ml-auto shrink-0"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="px-3 pb-3 pt-1">
              <button
                onClick={() => setProjectPickerSessionId(null)}
                className="w-full h-9 rounded-xl border border-gray-200 dark:border-[#2a2a2e] text-[13px] font-bold text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#1f1f22] transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
